import { ACCOUNT_DISCRIMINATOR_SIZE } from "@project-serum/anchor";
import {
  deserializeBorsh,
  getProposal,
  GovernanceConfig,
  GOVERNANCE_SCHEMA,
  InstructionData,
} from "@solana/spl-governance";
import { NATIVE_MINT } from "@solana/spl-token";
import { AnchorWallet } from "@solana/wallet-adapter-react";
import { LAMPORTS_PER_SOL, PublicKey } from "@solana/web3.js";
import {
  EMPTY_STRING,
  GOVERNANCE_CONFIG_SIZE,
} from "../../../common/constants/common.constants";
import {
  GoveranceType,
  ProposalAction,
  ProposalStatus,
  ProposalTypeProgramString,
} from "../../../common/enums/proposals.enums";
import { IUpdateRoleWeight } from "../../../common/interfaces/club-config.interface";
import {
  IMembersRights,
  IReservedRights,
  ITreasuryTokenAccInfo,
} from "../../../common/interfaces/clubs.interface";
import {
  IBasicProposalData,
  IProposalMetadataFromProgram,
  ITransferProposalMetadata,
  IUpdateGovernanceConfigProposalMetadata,
  IUpdateRoleConfigProposalMetadata,
  ITreasuryVaultProposalMetadata,
  IWithdrawalProposalMetadata,
  IAddSellPermissionProposal,
} from "../../../common/interfaces/proposals.interface";
import { getMintMetadata } from "../../methods/general";
import {
  escrowProgramFactory,
  magicEdenSeed,
  offerSeed,
  programFactory,
  proposalMetadataSeed,
  RPC_CONNECTION,
  unqClubSeed,
} from "../utilities";
import { getInfoForMintAccounts } from "./common.helpers";
import {
  mapProposalAction,
  mapProposalTypeFromProgram,
} from "./proposals.helpers";
import { getSingleWithdrawalAccount } from "./withdrawal.helpers";

export async function getBasicProposalInfo(
  proposal: PublicKey
): Promise<IBasicProposalData> {
  try {
    const proposalAccount = await getProposal(RPC_CONNECTION, proposal);
    const clubProgram = programFactory();
    const [proposalMetadata] = await PublicKey.findProgramAddress(
      [unqClubSeed, proposal.toBuffer(), proposalMetadataSeed],
      clubProgram.programId
    );
    const metadataAccount = await clubProgram.account.proposalMetadata.fetch(
      proposalMetadata
    );

    const proposalType = mapProposalTypeFromProgram(
      Object.keys(metadataAccount.proposalType)[0] as ProposalTypeProgramString
    );

    return {
      proposalAccount,
      proposalMetadata: {
        currentFundraiseIndex: metadataAccount.currentFundraiseIndex,
        description: metadataAccount.description,
        discussionLink: metadataAccount.discussionLink,
        governance: metadataAccount.governance,
        totalFinanacialPower: metadataAccount.totalFinancialPower.toNumber(),
        treasury: metadataAccount.treasury,
        proposalMetadata: proposalMetadata,
        voteThreshold: metadataAccount.approvalQuorumPercentage,
        proposalIndex: metadataAccount.proposalIndex,
        maxVoterWeight: metadataAccount.maxVoterWeight
          ? metadataAccount.maxVoterWeight.toNumber()
          : undefined,
        proposalStatus: Object.keys(
          metadataAccount.proposalStatus
        )[0] as ProposalStatus,
        proposalType,
        proposalAction: mapProposalAction(proposalType),
        createdAt: metadataAccount.createdAt.toNumber(),
      },
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export const parseProposalMetadata = (
  proposalMetadataAccount: any,
  proposalMetadataAddress: PublicKey
): IProposalMetadataFromProgram => {
  return {
    currentFundraiseIndex: proposalMetadataAccount.currentFundraiseIndex,
    description: proposalMetadataAccount.description,
    discussionLink: proposalMetadataAccount.discussionLink,
    governance: proposalMetadataAccount.governance,
    proposalAction: mapProposalAction(proposalMetadataAccount.proposalAction),
    proposalIndex: proposalMetadataAccount.proposalIndex,
    proposalMetadata: proposalMetadataAddress,
    voteThreshold: proposalMetadataAccount.approvalQuorumPercentage,
    maxVoterWeight: proposalMetadataAccount.maxVoterWeight
      ? proposalMetadataAccount.maxVoterWeight.toNumber()
      : undefined,
    proposalStatus:
      ProposalStatus[Object.keys(proposalMetadataAccount.proposalStatus)[0]],
    totalFinanacialPower:
      proposalMetadataAccount.totalFinancialPower.toNumber(),
    treasury: proposalMetadataAccount.treasury,
    proposalType: proposalMetadataAccount.proposalType,
    createdAt: proposalMetadataAccount.createdAt.toNumber(),
  };
};

export const getMagicEdenBuyProposalMetadata = async (
  proposalAction: ProposalAction,
  vaultBump: number,
  treasuryBump: number,
  instructionData: InstructionData
): Promise<ITreasuryVaultProposalMetadata> => {
  const escrowProgram = escrowProgramFactory();
  const clubProgram = programFactory();
  const [offer] = await PublicKey.findProgramAddress(
    [offerSeed, instructionData.accounts[1].pubkey.toBuffer()],
    escrowProgram.programId
  );

  let offeredAmount;

  try {
    const offerAccount = await escrowProgram.account.offer.fetch(offer);
    offeredAmount = offerAccount.offeredAmount.toNumber();
  } catch (error) {
    const [magicEdenAccounts] = await PublicKey.findProgramAddress(
      [unqClubSeed, offer.toBuffer(), magicEdenSeed],
      clubProgram.programId
    );
    const magicEdenAccountsAcc = await clubProgram.account.magicEdenData.fetch(
      magicEdenAccounts
    );
    const magicEdenData = magicEdenAccountsAcc.data as any;
    const offeredAmountInMeData = Buffer.from(
      magicEdenData[0].slice(
        magicEdenData[0].length - ACCOUNT_DISCRIMINATOR_SIZE,
        magicEdenData[0].length
      )
    );
    offeredAmount = offeredAmountInMeData.readUIntLE(0, 8);
  }

  let nftImgUrl;

  const wantedTokenMint = instructionData.accounts[3].pubkey;

  try {
    nftImgUrl = (await getMintMetadata(wantedTokenMint)).image;
  } catch (error) {
    console.log(error);

    nftImgUrl = EMPTY_STRING;
  }

  return {
    maker: instructionData.accounts[2].pubkey,
    nftImgUrl,
    offeredAmount,
    makerOfferedTokenAccount: instructionData.accounts[4].pubkey,
    makerWantedTokenAccount: instructionData.accounts[7].pubkey,
    offer: instructionData.accounts[0].pubkey,
    escrowedOfferedTokensAccountPda: instructionData.accounts[7].pubkey,
    offeredTokenMint: NATIVE_MINT,
    isMagicEdenProposal: true,
    wantedTokenMint: wantedTokenMint,
    wantedAmount: 1,
    proposalAction,
    signersBump:
      proposalAction === ProposalAction.BuyNft ? treasuryBump : vaultBump,
  };
};

export const getMagicEdenSellProposalMetadata = async (
  proposalInstruction: InstructionData,
  vaultAddress: PublicKey,
  vaultBumb: number
): Promise<ITreasuryVaultProposalMetadata> => {
  const clubProgram = programFactory();
  const escrowProgram = escrowProgramFactory();
  const [magicEdenAccountsHolder] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      proposalInstruction.accounts[0].pubkey.toBuffer(),
      magicEdenSeed,
    ],
    clubProgram.programId
  );

  let wantedAmount;

  try {
    const offerAccount = await escrowProgram.account.offer.fetch(
      proposalInstruction.accounts[0].pubkey
    );
    wantedAmount = offerAccount.wantedAmount.toNumber();
  } catch (error) {
    const magicEdenData = await clubProgram.account.magicEdenData.fetch(
      magicEdenAccountsHolder
    );

    const magicEdenDataArray = magicEdenData.data as any;
    const wantedAmountBuffer = Buffer.from(magicEdenDataArray[0].slice(10, 18));
    wantedAmount = wantedAmountBuffer.readUIntLE(0, 8);
  }

  const nftImgUrl = (
    await getMintMetadata(proposalInstruction.accounts[4].pubkey)
  ).image;

  return {
    proposalAction: ProposalAction.SellNft,
    maker: vaultAddress,
    offer: proposalInstruction.accounts[0].pubkey,
    wantedTokenMint: NATIVE_MINT,
    offeredTokenMint: proposalInstruction.accounts[4].pubkey,
    offeredAmount: 1,
    signersBump: vaultBumb,
    isMagicEdenProposal: true,
    nftImgUrl,
    makerWantedTokenAccount: proposalInstruction.accounts[6].pubkey,
    wantedAmount,
  };
};

export const getP2PProposalMetadata = async (
  treasury: PublicKey,
  valutAddress: PublicKey,
  proposalAction: ProposalAction,
  vaultBump: number,
  treasuryBump: number,
  proposalInstruction: InstructionData
): Promise<ITreasuryVaultProposalMetadata> => {
  const rawOfferedAmount: Uint8Array = proposalInstruction.data.slice(8, 16);
  const offeredAmountBuffer = Buffer.from(rawOfferedAmount);
  const offeredAmount = offeredAmountBuffer.readUIntLE(
    0,
    rawOfferedAmount.length
  );

  const rawWantedAmount: Uint8Array = proposalInstruction.data.slice(16, 24);
  const wantedAmountBuffer = Buffer.from(rawWantedAmount);
  const wantedAmount = wantedAmountBuffer.readUIntLE(0, rawWantedAmount.length);
  let nftImgUrl;
  try {
    const nftMetadataJson = await getMintMetadata(
      proposalAction === ProposalAction.BuyNft
        ? proposalInstruction.accounts[6].pubkey
        : proposalInstruction.accounts[5].pubkey
    );

    nftImgUrl = nftMetadataJson.image;
  } catch (error) {
    console.log(error);
  }

  const mintsInfo = await getInfoForMintAccounts([
    proposalAction === ProposalAction.BuyNft
      ? proposalInstruction.accounts[5].pubkey
      : proposalInstruction.accounts[6].pubkey,
  ]);

  return {
    maker: proposalAction === ProposalAction.BuyNft ? treasury : valutAddress, //proposalInstruction.accounts[2].pubkey
    makerOfferedTokenAccount: proposalInstruction.accounts[4].pubkey,
    makerWantedTokenAccount: proposalInstruction.accounts[8].pubkey,
    offer: proposalInstruction.accounts[0].pubkey,
    escrowedOfferedTokensAccountPda: proposalInstruction.accounts[7].pubkey,
    offeredTokenMint: proposalInstruction.accounts[5].pubkey,
    wantedTokenMint: proposalInstruction.accounts[6].pubkey,
    offeredAmount,
    wantedAmount,
    proposalAction,
    signersBump:
      proposalAction === ProposalAction.BuyNft ? treasuryBump : vaultBump,
    nftImgUrl: nftImgUrl,
    mintSymbol: mintsInfo[0].symbol,
    mintDecimals: mintsInfo[0].decimals,
  };
};

export const getWithdrawalProposalMetadata = async (
  treasury: PublicKey,
  realmName: string,
  wallet: AnchorWallet,
  denominatedCurrency: ITreasuryTokenAccInfo,
  memberRights: IMembersRights,
  proposal: PublicKey,
  proposalInstruction: InstructionData,
  fundraiseIndex: number,
  timestamp: number,
  reservedRights: IReservedRights | undefined
): Promise<IWithdrawalProposalMetadata> => {
  const rawWithdrawalAmount: Uint8Array = proposalInstruction.data.slice(8, 16);
  const offeredAmountBuffer = Buffer.from(rawWithdrawalAmount);
  const offeredAmount = offeredAmountBuffer.readUIntLE(
    0,
    rawWithdrawalAmount.length
  );

  const mintsInfo = await getInfoForMintAccounts([
    proposalInstruction.accounts[6].pubkey,
  ]);

  const { memberWithdrawalData, totalFinancialPower } =
    await getSingleWithdrawalAccount(
      realmName,
      proposal,
      treasury,
      wallet,
      offeredAmount,
      memberRights,
      proposalInstruction.accounts[6].pubkey,
      mintsInfo[0].decimals,
      mintsInfo[0].symbol,
      denominatedCurrency,
      fundraiseIndex,
      timestamp,
      reservedRights
    );

  return {
    withdrawalAmount: offeredAmount,
    proposalAction: ProposalAction.Withdrawal,
    withdrawal: proposalInstruction.accounts[4].pubkey,
    withdrawalData: proposalInstruction.accounts[5].pubkey,
    treasuryToken: proposalInstruction.accounts[3].pubkey,
    withdrawalMembersInfo: memberWithdrawalData,
    totalFinancialPower,
    withdrawalMint: proposalInstruction.accounts[6].pubkey,
    mintSymbol: mintsInfo[0].symbol,
    mintDecimals: mintsInfo[0].decimals,
  };
};

export const getTransferProposalMetadata = async (
  proposalInstruction: InstructionData
): Promise<ITransferProposalMetadata> => {
  const rawAmountToInvest: Uint8Array = proposalInstruction.data.slice(8, 16);
  const amountToInvestBuffer = Buffer.from(rawAmountToInvest);
  const amountToInvest = amountToInvestBuffer.readUIntLE(
    0,
    rawAmountToInvest.length
  );

  const tokenInfo: any = await RPC_CONNECTION.getParsedAccountInfo(
    proposalInstruction.accounts[3].pubkey
  );
  const mintsInfo = await getInfoForMintAccounts([
    tokenInfo.value?.data.parsed.info.mint,
  ]);
  return {
    proposalAction: ProposalAction.Transfer,
    offer: proposalInstruction.accounts[1].pubkey,
    treasuryToken: proposalInstruction.accounts[3].pubkey,
    destination: proposalInstruction.accounts[4].pubkey,
    amountToInvest: amountToInvest,
    mint: tokenInfo.value?.data.parsed.info.mint,
    mintLogo: mintsInfo[0].logoURI,
    mintSymbol: mintsInfo[0].symbol,
  };
};

export const getSolseProposalMetadata = async (
  proposalAction: ProposalAction,
  vaultBump: number,
  treasuryBump: number,
  proposalInstruction: InstructionData
): Promise<ITreasuryVaultProposalMetadata> => {
  const instructionData = proposalInstruction.data;
  const wantedAmount = Buffer.from(instructionData.slice(8, 16)).readUIntLE(
    0,
    8
  );

  const offeredAmount = Buffer.from(instructionData.slice(16, 24)).readUIntLE(
    0,
    8
  );

  const sellerFeeBps = Buffer.from(instructionData.slice(24, 26)).readInt16LE(
    0
  );

  const nftMint =
    proposalInstruction.accounts[
      proposalAction === ProposalAction.BuyNft ? 4 : 3
    ].pubkey;
  const nftImgUrl = (await getMintMetadata(nftMint)).image;

  return {
    maker: proposalInstruction.accounts[7].pubkey,
    makerWantedTokenAccount: proposalInstruction.accounts[6].pubkey,
    offer: proposalInstruction.accounts[0].pubkey,
    escrowedOfferedTokensAccountPda: proposalInstruction.accounts[7].pubkey,
    offeredTokenMint: proposalInstruction.accounts[3].pubkey,
    wantedTokenMint: proposalInstruction.accounts[4].pubkey,
    offeredAmount,
    wantedAmount,
    proposalAction,
    signersBump:
      proposalAction === ProposalAction.BuyNft ? treasuryBump : vaultBump,
    nftImgUrl,
    sellerFeeBps,
  };
};

export const getUpdateGovernanceConfigProposalMetadata = async (
  proposalInstructions: InstructionData[],
  treasuryDataAddress: PublicKey
): Promise<IUpdateGovernanceConfigProposalMetadata> => {
  const governancesToUpdate: PublicKey[] = [];
  const relatedGovernedAccounts: PublicKey[] = [];

  const ix = proposalInstructions[0];

  const configsAmount = Buffer.from(ix.data.slice(1, 5)).readUInt32LE(0);

  const configs: GovernanceConfig[] = [];

  const clubProgram = programFactory();

  for (let i = 0; i < configsAmount; i++) {
    const bytes = ix.data.slice(
      5 + GOVERNANCE_CONFIG_SIZE * i,
      5 + GOVERNANCE_CONFIG_SIZE * i + GOVERNANCE_CONFIG_SIZE
    );

    const governanceConfig: GovernanceConfig = deserializeBorsh(
      GOVERNANCE_SCHEMA,
      GovernanceConfig,
      Buffer.from(bytes)
    );
    configs.push(governanceConfig);
  }

  const newQuorums = configs.map(
    (config) => config.voteThresholdPercentage.value
  );

  for (let i = 0; i < ix.accounts.length; i += 2) {
    governancesToUpdate.push(ix.accounts[i].pubkey);
    relatedGovernedAccounts.push(ix.accounts[i + 1].pubkey);
  }

  let newVotingTime: number | undefined;

  if (configs.length === 1) {
    newVotingTime = (configs[0].maxVotingTime - 1) / 86400;
  }

  const treasury = await clubProgram.account.treasuryData.fetch(
    treasuryDataAddress
  );

  let governanceType: GoveranceType = GoveranceType.DefaultGovernance;

  switch (proposalInstructions[0].accounts[0].pubkey.toString()) {
    case treasury.changeClubConfigGovernance?.toBase58(): {
      governanceType = GoveranceType.ClubConfigGovernance;
      break;
    }
    case treasury.withdrawalGovernance?.toString(): {
      governanceType = GoveranceType.WithdrawalGovernance;
      break;
    }
    case treasury.transferGovernance?.toString(): {
      governanceType = GoveranceType.TransferGovernance;
      break;
    }
    case treasury.governance.toString(): {
      governanceType = GoveranceType.DefaultGovernance;
      break;
    }
    default: {
      governanceType = GoveranceType.TradeApprovalGovernance;
    }
  }

  return {
    proposalAction: ProposalAction.UpdateGovernanceConfig,
    governancesToUpdate,
    newQuorums,
    governedAccountsToUpdate: relatedGovernedAccounts,
    newVotingTime,
    governanceType,
  };
};

export const getUpdateRoleConfigProposalMetadata = async (
  proposalInstruction: InstructionData
): Promise<IUpdateRoleConfigProposalMetadata> => {
  let instructionData = Buffer.from(
    proposalInstruction.data.slice(8, proposalInstruction.data.length)
  );

  instructionData = instructionData.slice(4, instructionData.length);
  const updateRole: IUpdateRoleWeight[] = [];

  let skipNumber = 0;

  for (
    let i = 0;
    i <
    proposalInstruction.data.slice(8, proposalInstruction.data.length).length;
    i += skipNumber
  ) {
    try {
      const stringLen = instructionData.slice(0, 4).readUInt32LE(0);
      const role = instructionData.slice(4, 4 + stringLen).toString();
      const weight = instructionData
        .slice(4 + stringLen, 4 + stringLen + 8)
        .readUIntLE(0, 8);
      const updateWeight = instructionData
        .slice(4 + 8 + stringLen, 4 + 8 + stringLen + 8)
        .readUIntLE(0, 8);
      skipNumber = 4 + 8 + 8 + stringLen;
      instructionData = instructionData.slice(
        skipNumber,
        instructionData.length
      );
      updateRole.push({
        currentWeight: weight / LAMPORTS_PER_SOL,
        role,
        updateWeight: updateWeight / LAMPORTS_PER_SOL,
      });
    } catch (error) {}
  }

  return {
    proposalAction: ProposalAction.UpdateRoleConfig,
    roleConfigData: updateRole,
  };
};

export const getSellPermissionProposal = async (
  proposalTransaction: InstructionData
): Promise<IAddSellPermissionProposal> => {
  const instructionData = proposalTransaction.data;

  const from = Number(
    Buffer.from(instructionData.slice(0, 8)).readUIntLE(0, 8)
  );
  const to = Number(Buffer.from(instructionData.slice(8, 16)).readUIntLE(0, 8));

  const quorum = Buffer.from(instructionData.slice(16, 17)).readUInt8(0);

  const decimals = Buffer.from(instructionData.slice(17, 19)).readUInt8(0);

  const maxVotingTime = Buffer.from(instructionData.slice(19, 23)).readUInt32LE(
    0
  );

  return {
    maxVotingTime,
    sellPermissionData: {
      from,
      to,
      votingQuorum: quorum,
    },
    proposalAction: ProposalAction.AddSellPermission,
  };
};
